import React, { useEffect, useState } from 'react';
import { Plus, Search, Eye, Edit } from 'lucide-react';
import { Card, Btn, Input, Select, Table, Spinner, ErrBox } from '../../../components/UI';
import { contractAPI } from '../../../utils/api';
import { fmtRp, fmtDate } from '../../../utils/format';

const statusCfg = {
  DRAFT: { bg: '#F3F4F6', color: '#374151' },
  MENUNGGU_KONTRAK: { bg: '#FEF3C7', color: '#92400E' },
  AKTIF: { bg: '#DCFCE7', color: '#166534' },
  DALAM_PELAKSANAAN: { bg: '#DBEAFE', color: '#1E40AF' },
  SELESAI: { bg: '#ECFDF5', color: '#059669' },
  EXPIRED: { bg: '#FEE2E2', color: '#991B1B' },
  DIBATALKAN: { bg: '#FEF2F2', color: '#DC2626' },
};

const tipeCfg = {
  KONTRAK: { bg: '#EDE9FE', color: '#5B21B6' },
  SPK: { bg: '#DBEAFE', color: '#1E40AF' },
  MOU: { bg: '#FEF3C7', color: '#92400E' },
  LOI: { bg: '#F3F4F6', color: '#374151' },
};

export default function KontrakListTab({ onEdit, onNew, tipeFilter }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 15;

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, limit, search, status });
      if (tipeFilter) params.set('tipe_kontrak', tipeFilter);
      const r = await contractAPI.getAll(`?${params.toString()}`);
      setContracts(r.data || []);
      setTotal(r.pagination?.total || 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page, status]);

  const handleSearch = () => load();

  const cols = [
    { key: 'contract_no', label: 'No. Kontrak', render: (_, r) => <span style={{ fontWeight: 600, fontSize: 13 }}>{r.contract_no}</span> },
    { key: 'tipe_kontrak', label: 'Tipe', align: 'center', render: (_, r) => {
      const c = tipeCfg[r.tipe_kontrak] || tipeCfg.LOI;
      return <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: c.bg, color: c.color }}>{r.tipe_kontrak || '-'}</span>;
    }},
    { key: 'judul', label: 'Judul', render: (_, r) => <span style={{ fontSize: 13 }}>{r.judul}</span> },
    { key: 'vendor_nama', label: 'Vendor', render: (_, r) => (
      <div style={{ fontSize: 12 }}>
        <span style={{ fontWeight: 500 }}>{r.vendor_nama}</span>
        <br /><span style={{ color: '#9CA3AF' }}>{r.vendor_kode}</span>
      </div>
    )},
    { key: 'nilai_kontrak', label: 'Nilai Kontrak', render: (_, r) => (
      <div style={{ fontSize: 13 }}>
        <span style={{ fontWeight: 600 }}>{fmtRp(Number(r.nilai_kontrak) || 0)}</span>
        <br /><span style={{ fontSize: 11, color: '#059669' }}>Sisa: {fmtRp(Number(r.sisa_nilai) || 0)}</span>
      </div>
    )},
    { key: 'tanggal_mulai', label: 'Berlaku', render: (_, r) => (
      <div style={{ fontSize: 12 }}>
        <span>{fmtDate(r.tanggal_mulai)}</span>
        <br /><span style={{ color: '#9CA3AF' }}>s/d {fmtDate(r.tanggal_selesai)}</span>
      </div>
    )},
    { key: 'akun_kode', label: 'COA', render: (_, r) => (
      <div style={{ fontSize: 11 }}>
        {r.program_kode && <span style={{ display: 'inline-block', padding: '1px 5px', borderRadius: 4, background: '#EDE9FE', color: '#5B21B6', marginRight: 2 }}>{r.program_kode}</span>}
        {r.kegiatan_kode && <span style={{ display: 'inline-block', padding: '1px 5px', borderRadius: 4, background: '#FEF3C7', color: '#92400E', marginRight: 2 }}>{r.kegiatan_kode}</span>}
        {r.kro_kode && <span style={{ display: 'inline-block', padding: '1px 5px', borderRadius: 4, background: '#D1FAE5', color: '#065F46', marginRight: 2 }}>{r.kro_kode}</span>}
        {r.akun_kode && <span style={{ display: 'inline-block', padding: '1px 5px', borderRadius: 4, background: '#DBEAFE', color: '#1E3A8A', marginRight: 2 }}>{r.akun_kode}</span>}
      </div>
    )},
    { key: 'status_kontrak', label: 'Status', render: (_, r) => {
      const c = statusCfg[r.status_kontrak] || { bg: '#F3F4F6', color: '#374151' };
      return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: c.bg, color: c.color }}>{r.status_kontrak}</span>;
    }},
    { key: 'id', label: 'Aksi', render: (_, r) => (
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => onEdit(r.id)} title="Edit" style={{ padding: '4px 6px', border: '1px solid #E5E7EB', borderRadius: 6, background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <Edit size={13} color="#374151" />
        </button>
      </div>
    )},
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} placeholder="Cari no. kontrak, judul, vendor…" style={{ maxWidth: 280 }} />
        <Select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} options={[
          { value: '', label: 'Semua Status' },
          { value: 'DRAFT', label: 'Draft' },
          { value: 'MENUNGGU_KONTRAK', label: 'Menunggu Kontrak' },
          { value: 'AKTIF', label: 'Aktif' },
          { value: 'DALAM_PELAKSANAAN', label: 'Dalam Pelaksanaan' },
          { value: 'SELESAI', label: 'Selesai' },
          { value: 'EXPIRED', label: 'Expired' },
          { value: 'DIBATALKAN', label: 'Dibatalkan' },
        ]} style={{ maxWidth: 200 }} />
        <Btn onClick={handleSearch}>Cari</Btn>
        <div style={{ flex: 1 }} />
        <Btn onClick={onNew}>+ Kontrak Baru</Btn>
      </div>

      {loading ? <Spinner /> : error ? <ErrBox msg={error} onRetry={load} /> : (
        <>
          <Table cols={cols} data={contracts} />
          {total > limit && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <Btn variant="secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹ Prev</Btn>
              <span style={{ padding: '8px 12px', fontSize: 13, color: '#6B7280' }}>Halaman {page} dari {Math.ceil(total / limit)}</span>
              <Btn variant="secondary" onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / limit)}>Next ›</Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
}
